/**
 * Data Ingestion Admin Page - Phase 9
 * Manages data sources, CSV imports, review queue, and import history
 * Fixed for light mode compatibility
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import {
  Database,
  Upload,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  FileSpreadsheet,
  ArrowRight,
  Warning,
  CaretDown,
  MagnifyingGlass,
  Funnel,
  ArrowClockwise,
  Trash,
  PencilSimple,
  Buildings,
  MapPin,
  Calendar,
  CurrencyEur,
  CheckSquare,
  X,
  Info,
  ListChecks,
  CloudArrowUp,
  ClockClockwise,
} from '@phosphor-icons/react';

import { importApi } from '../utils/api';
import { formatCurrency, formatDate } from '../utils/formatters';
import { PageHelpBlock } from '../components/help';
import { pageExplanations } from '../content/helpContent';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { cn } from '../lib/utils';

// ============== STATUS BADGES ==============

const StatusBadge = ({ status }) => {
  const variants = {
    pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    needs_review: 'bg-orange-100 text-orange-800 border-orange-300',
    approved: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    rejected: 'bg-red-100 text-red-800 border-red-300',
    merged: 'bg-blue-100 text-blue-800 border-blue-300',
    duplicate: 'bg-purple-100 text-purple-800 border-purple-300',
  };

  const labels = {
    pending: 'En attente',
    needs_review: 'À vérifier',
    approved: 'Approuvé',
    rejected: 'Rejeté',
    merged: 'Converti',
    duplicate: 'Doublon',
  };

  return (
    <span className={cn("px-2 py-0.5 text-xs font-medium rounded border", variants[status] || variants.pending)}>
      {labels[status] || status}
    </span>
  );
};

const SourceTypeBadge = ({ type }) => {
  const variants = {
    official: 'bg-emerald-100 text-emerald-700',
    benchmark: 'bg-blue-100 text-blue-700',
    listing: 'bg-amber-100 text-amber-700',
    manual: 'bg-slate-100 text-slate-700',
    import: 'bg-violet-100 text-violet-700',
    api: 'bg-cyan-100 text-cyan-700',
  };

  return (
    <Badge variant="outline" className={cn("text-xs", variants[type] || variants.import)}>
      {type}
    </Badge>
  );
};

const ObservationTypeBadge = ({ type }) => {
  const variants = {
    asking: 'bg-amber-100 text-amber-700',
    sold: 'bg-emerald-100 text-emerald-700',
    benchmark: 'bg-blue-100 text-blue-700',
    estimated: 'bg-slate-100 text-slate-700',
  };

  return (
    <Badge variant="outline" className={cn("text-xs", variants[type] || variants.asking)}>
      {type}
    </Badge>
  );
};

const ConfidenceBar = ({ score, label }) => {
  const color = score >= 0.7 ? 'bg-emerald-500' : score >= 0.5 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
        <div className={cn("h-full rounded-full", color)} style={{ width: `${score * 100}%` }} />
      </div>
      <span className="text-xs text-muted-foreground">{label || `${Math.round(score * 100)}%`}</span>
    </div>
  );
};

// ============== TABS ==============

const Tabs = ({ tabs, activeTab, onChange }) => (
  <div className="flex gap-1 p-1 bg-muted rounded-lg">
    {tabs.map((tab) => (
      <button
        key={tab.id}
        onClick={() => onChange(tab.id)}
        data-testid={`tab-${tab.id}`}
        className={cn(
          "px-4 py-2 text-sm font-medium rounded-md transition-all",
          activeTab === tab.id
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground hover:text-foreground hover:bg-background"
        )}
      >
        {tab.icon && <tab.icon size={16} className="inline mr-2" />}
        {tab.label}
        {tab.count !== undefined && (
          <span className={cn(
            "ml-2 px-1.5 py-0.5 text-xs rounded",
            activeTab === tab.id ? "bg-primary-foreground/20" : "bg-muted-foreground/20"
          )}>
            {tab.count}
          </span>
        )}
      </button>
    ))}
  </div>
);

// ============== DATA SOURCES PANEL ==============

const DataSourcesPanel = ({ sources, loading, onRefresh }) => {
  if (loading) {
    return <div className="text-muted-foreground text-center py-8">Chargement des sources...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Sources de Données Configurées</h3>
        <Button variant="ghost" size="sm" onClick={onRefresh}>
          <ArrowClockwise size={18} />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sources.map((source) => (
          <Card key={source.id} data-testid={`source-${source.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-medium">{source.name}</h4>
                  <p className="text-xs text-muted-foreground mt-0.5">{source.id}</p>
                </div>
                <SourceTypeBadge type={source.source_type} />
              </div>

              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{source.description}</p>

              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Method:</span>
                  <span>{source.ingestion_method}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Confidence:</span>
                  <span>{Math.round(source.default_confidence * 100)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Trust:</span>
                  <span className={cn(
                    "capitalize font-medium",
                    source.trust_level === 'high' ? 'text-emerald-600' :
                    source.trust_level === 'medium' ? 'text-amber-600' : 'text-red-600'
                  )}>
                    {source.trust_level}
                  </span>
                </div>
                {source.last_run && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Last run:</span>
                    <span>{formatDate(source.last_run)}</span>
                  </div>
                )}
              </div>

              {source.limitations && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs text-amber-600 flex items-start gap-1">
                    <Warning size={12} className="mt-0.5 flex-shrink-0" />
                    <span className="line-clamp-2">{source.limitations}</span>
                  </p>
                </div>
              )}

              {source.last_batch_stats && (
                <div className="mt-3 pt-3 border-t flex gap-2 text-xs">
                  <span className="text-muted-foreground">Last batch:</span>
                  <span className="text-emerald-600">{source.last_batch_stats.approved} approved</span>
                  <span className="text-amber-600">{source.last_batch_stats.pending} pending</span>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ============== CSV IMPORT WIZARD ==============

const CSVImportWizard = ({ onImportComplete }) => {
  const [step, setStep] = useState(1);
  const [csvContent, setCsvContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [delimiter, setDelimiter] = useState(',');
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [observationType, setObservationType] = useState('asking');
  const [sourceId, setSourceId] = useState('csv_upload');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result;
      setCsvContent(content);
      handlePreview(content);
    };
    reader.readAsText(file);
  };

  const handlePreview = async (content) => {
    try {
      const previewData = await importApi.previewCsv(content, delimiter);
      setPreview(previewData);
      setMapping(previewData.suggested_mapping || {});
      setStep(2);
    } catch (err) {
      toast.error('Échec de l\'analyse du fichier CSV');
      console.error(err);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const importResult = await importApi.importCsv(
        csvContent,
        mapping,
        sourceId,
        observationType,
        delimiter
      );
      setResult(importResult);
      setStep(4);
      toast.success(`${importResult.records_imported} enregistrements importés`);
      onImportComplete?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Échec de l\'importation');
      console.error(err);
    } finally {
      setImporting(false);
    }
  };

  const resetWizard = () => {
    setStep(1);
    setCsvContent('');
    setFileName('');
    setPreview(null);
    setMapping({});
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const fieldOptions = [
    { key: 'address', label: 'Address' },
    { key: 'arrondissement', label: 'Arrondissement' },
    { key: 'postal_code', label: 'Postal Code' },
    { key: 'surface', label: 'Surface (m²)' },
    { key: 'rooms', label: 'Rooms' },
    { key: 'floor', label: 'Floor' },
    { key: 'property_type', label: 'Property Type' },
    { key: 'asking_price', label: 'Asking Price' },
    { key: 'sold_price', label: 'Sold Price' },
    { key: 'observation_date', label: 'Date' },
    { key: 'condition', label: 'Condition' },
    { key: 'elevator', label: 'Elevator' },
    { key: 'balcony', label: 'Balcony' },
    { key: 'terrace', label: 'Terrace' },
    { key: 'parking', label: 'Parking' },
    { key: 'source_url', label: 'Source URL' },
    { key: 'notes', label: 'Notes' },
  ];

  return (
    <Card>
      <CardContent className="p-6">
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2, 3, 4].map((s) => (
            <React.Fragment key={s}>
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
                step >= s ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              )}>
                {s}
              </div>
              {s < 4 && <div className={cn("w-8 h-0.5", step > s ? "bg-primary" : "bg-muted")} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="text-center">
            <CloudArrowUp size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Télécharger un fichier CSV</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Sélectionnez un fichier CSV contenant les données des comparables
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="csv-file-input"
            />

            <Button onClick={() => fileInputRef.current?.click()} data-testid="upload-csv-btn">
              <Upload size={18} className="mr-2" />
              Sélectionner Fichier CSV
            </Button>

            <div className="mt-4 flex items-center justify-center gap-4 text-sm">
              <Label className="text-muted-foreground">
                Delimiter:
                <select
                  value={delimiter}
                  onChange={(e) => setDelimiter(e.target.value)}
                  className="ml-2 bg-background border border-input rounded px-2 py-1"
                >
                  <option value=",">Comma (,)</option>
                  <option value=";">Semicolon (;)</option>
                  <option value="\t">Tab</option>
                </select>
              </Label>
            </div>
          </div>
        )}

        {/* Step 2: Preview & Map */}
        {step === 2 && preview && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Map Columns</h3>
                <p className="text-sm text-muted-foreground">{fileName} - {preview.total_rows} rows detected</p>
              </div>
              <Button variant="ghost" size="sm" onClick={resetWizard}>
                Start Over
              </Button>
            </div>

            {/* Validation Summary */}
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-3 text-center">
                  <div className="text-2xl font-bold">{preview.validation_summary?.rows_with_address || 0}</div>
                  <div className="text-xs text-muted-foreground">With Address</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <div className="text-2xl font-bold">{preview.validation_summary?.rows_with_price || 0}</div>
                  <div className="text-xs text-muted-foreground">With Price</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <div className="text-2xl font-bold">{preview.validation_summary?.rows_with_surface || 0}</div>
                  <div className="text-xs text-muted-foreground">With Surface</div>
                </CardContent>
              </Card>
            </div>

            {/* Column Mapping */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium">Column Mapping</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {fieldOptions.map((field) => (
                  <div key={field.key} className="space-y-1">
                    <Label className="text-xs">{field.label}</Label>
                    <select
                      value={mapping[field.key] || ''}
                      onChange={(e) => setMapping({ ...mapping, [field.key]: e.target.value || null })}
                      className="w-full bg-background border border-input rounded px-2 py-1.5 text-sm"
                    >
                      <option value="">-- Not mapped --</option>
                      {preview.columns_detected?.map((col) => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            {/* Import Settings */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Observation Type</Label>
                <select
                  value={observationType}
                  onChange={(e) => setObservationType(e.target.value)}
                  className="w-full bg-background border border-input rounded px-2 py-1.5 text-sm mt-1"
                >
                  <option value="asking">Asking Price</option>
                  <option value="sold">Sold Price</option>
                  <option value="benchmark">Benchmark</option>
                  <option value="estimated">Estimated</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Source</Label>
                <select
                  value={sourceId}
                  onChange={(e) => setSourceId(e.target.value)}
                  className="w-full bg-background border border-input rounded px-2 py-1.5 text-sm mt-1"
                >
                  <option value="csv_upload">CSV Upload</option>
                  <option value="seloger">SeLoger Export</option>
                  <option value="pap">PAP Export</option>
                  <option value="leboncoin">LeBonCoin Export</option>
                  <option value="partner">Partner Data</option>
                </select>
              </div>
            </div>

            {/* Sample Preview */}
            {preview.sample_rows?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">Sample Data</h4>
                <div className="overflow-x-auto border rounded">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted">
                        {preview.columns_detected?.slice(0, 6).map((col) => (
                          <th key={col} className="text-left py-2 px-2 font-medium">{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.sample_rows.slice(0, 3).map((row, i) => (
                        <tr key={i} className="border-b">
                          {preview.columns_detected?.slice(0, 6).map((col) => (
                            <td key={col} className="py-2 px-2 truncate max-w-[150px]">
                              {row[col] || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={resetWizard}>Annuler</Button>
              <Button onClick={() => setStep(3)} disabled={!mapping.address} data-testid="continue-import-btn">
                Continuer
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Confirm Import */}
        {step === 3 && (
          <div className="text-center space-y-6">
            <div>
              <ListChecks size={48} className="mx-auto text-primary mb-4" />
              <h3 className="text-lg font-semibold">Ready to Import</h3>
              <p className="text-sm text-muted-foreground mt-2">
                {preview?.total_rows} records will be imported from {fileName}
              </p>
            </div>

            <Card className="max-w-md mx-auto">
              <CardContent className="p-4 text-left space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Source:</span>
                  <span>{sourceId}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Type:</span>
                  <ObservationTypeBadge type={observationType} />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Address column:</span>
                  <span>{mapping.address || 'Not set'}</span>
                </div>
              </CardContent>
            </Card>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left max-w-md mx-auto">
              <p className="text-sm text-amber-700 flex items-start gap-2">
                <Info size={16} className="mt-0.5 flex-shrink-0" />
                Les enregistrements entreront dans la file d'attente. Ils doivent être approuvés avant d'être utilisés dans l'analyse.
              </p>
            </div>

            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
              <Button onClick={handleImport} disabled={importing} data-testid="confirm-import-btn">
                {importing ? 'Importing...' : 'Import Records'}
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Result */}
        {step === 4 && result && (
          <div className="text-center space-y-6">
            <div>
              <CheckCircle size={48} className="mx-auto text-emerald-500 mb-4" />
              <h3 className="text-lg font-semibold">Import Complete</h3>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-xl mx-auto">
              <Card>
                <CardContent className="p-3 text-center">
                  <div className="text-2xl font-bold text-emerald-600">{result.records_imported}</div>
                  <div className="text-xs text-muted-foreground">Imported</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <div className="text-2xl font-bold text-amber-600">{result.records_pending_review}</div>
                  <div className="text-xs text-muted-foreground">En Attente de Révision</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <div className="text-2xl font-bold text-purple-600">{result.records_duplicate || 0}</div>
                  <div className="text-xs text-muted-foreground">Duplicates</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <div className="text-2xl font-bold text-red-600">{result.records_rejected}</div>
                  <div className="text-xs text-muted-foreground">Rejeté</div>
                </CardContent>
              </Card>
            </div>

            {result.errors?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left max-w-xl mx-auto">
                <h4 className="text-sm font-medium text-red-700 mb-2">Erreurs ({result.errors.length})</h4>
                <ul className="text-xs text-red-600 space-y-1 max-h-32 overflow-y-auto">
                  {result.errors.slice(0, 10).map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                  {result.errors.length > 10 && <li>...et {result.errors.length - 10} de plus</li>}
                </ul>
              </div>
            )}

            <Button onClick={resetWizard} data-testid="import-another-btn">
              Importer un autre fichier
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============== REVIEW QUEUE ==============

const ReviewQueue = ({ onRecordAction }) => {
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [detailRecord, setDetailRecord] = useState(null);
  const [editRecord, setEditRecord] = useState(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const data = await importApi.getQueue(params);
      setRecords(data.records || []);
      setStats(data.stats || {});
    } catch (err) {
      toast.error('Échec du chargement de la file');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const handleApprove = async (recordId) => {
    try {
      await importApi.approveRecord(recordId);
      toast.success('Enregistrement approuvé');
      fetchQueue();
      onRecordAction?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Échec de l\'approbation');
    }
  };

  const handleReject = async (recordId) => {
    try {
      await importApi.rejectRecord(recordId);
      toast.success('Enregistrement rejeté');
      fetchQueue();
      onRecordAction?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Échec du rejet');
    }
  };

  const handleConvert = async (recordId) => {
    try {
      const result = await importApi.convertToComparable(recordId);
      toast.success(`Converti en comparable ${result.comparable_id}`);
      fetchQueue();
      onRecordAction?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Échec de la conversion');
    }
  };

  const handleBulkApprove = async () => {
    if (selectedRecords.length === 0) return;
    try {
      await importApi.bulkApprove(selectedRecords);
      toast.success(`${selectedRecords.length} enregistrements approuvés`);
      setSelectedRecords([]);
      fetchQueue();
      onRecordAction?.();
    } catch (err) {
      toast.error('Échec de l\'approbation en masse');
    }
  };

  const handleBulkReject = async () => {
    if (selectedRecords.length === 0) return;
    try {
      await importApi.bulkReject(selectedRecords);
      toast.success(`${selectedRecords.length} enregistrements rejetés`);
      setSelectedRecords([]);
      fetchQueue();
      onRecordAction?.();
    } catch (err) {
      toast.error('Échec du rejet en masse');
    }
  };

  const handleSaveEdit = async (recordId, updates) => {
    try {
      await importApi.updateRecord(recordId, updates);
      toast.success('Enregistrement mis à jour');
      setEditRecord(null);
      fetchQueue();
    } catch (err) {
      toast.error('Échec de la mise à jour');
    }
  };

  const toggleSelectAll = () => {
    if (selectedRecords.length === records.length) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(records.map((r) => r.id));
    }
  };

  const toggleSelect = (id) => {
    if (selectedRecords.includes(id)) {
      setSelectedRecords(selectedRecords.filter((r) => r !== id));
    } else {
      setSelectedRecords([...selectedRecords, id]);
    }
  };

  return (
    <div className="space-y-4">
      {/* Stats Row */}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-4">
        {[
          { key: 'pending', label: 'En Attente', color: 'amber' },
          { key: 'needs_review', label: 'À Réviser', color: 'orange' },
          { key: 'approved', label: 'Approuvé', color: 'emerald' },
          { key: 'rejected', label: 'Rejeté', color: 'red' },
          { key: 'duplicate', label: 'Doublon', color: 'purple' },
        ].map((s, idx) => (
          <button
            key={s.key}
            onClick={() => setStatusFilter(statusFilter === s.key ? '' : s.key)}
            className={cn(
              "rounded-lg p-2 sm:p-3 text-center transition-all border",
              statusFilter === s.key ? "ring-2 ring-primary" : "",
              // Hide last 2 on very small screens
              idx >= 3 && "hidden sm:block"
            )}
          >
            <div className={cn("text-lg sm:text-xl font-bold", `text-${s.color}-600`)}>{stats[s.key] || 0}</div>
            <div className="text-[10px] sm:text-xs text-muted-foreground">{s.label}</div>
          </button>
        ))}
      </div>

      {/* Mobile-only: Show rejected/duplicate in a row */}
      <div className="sm:hidden grid grid-cols-2 gap-2">
        {[
          { key: 'rejected', label: 'Rejeté', color: 'red' },
          { key: 'duplicate', label: 'Doublon', color: 'purple' },
        ].map((s) => (
          <button
            key={s.key}
            onClick={() => setStatusFilter(statusFilter === s.key ? '' : s.key)}
            className={cn(
              "rounded-lg p-2 text-center transition-all border",
              statusFilter === s.key ? "ring-2 ring-primary" : ""
            )}
          >
            <div className={cn("text-lg font-bold", `text-${s.color}-600`)}>{stats[s.key] || 0}</div>
            <div className="text-[10px] text-muted-foreground">{s.label}</div>
          </button>
        ))}
      </div>

      {/* Actions Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={fetchQueue}>
            <ArrowClockwise size={18} />
          </Button>
          {selectedRecords.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{selectedRecords.length} selected</span>
              <Button size="sm" variant="outline" className="text-emerald-600" onClick={handleBulkApprove}>
                Approve All
              </Button>
              <Button size="sm" variant="outline" className="text-red-600" onClick={handleBulkReject}>
                Reject All
              </Button>
            </div>
          )}
        </div>
        {statusFilter && (
          <Button variant="ghost" size="sm" onClick={() => setStatusFilter('')}>
            Effacer Filtre
          </Button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Chargement...</div>
      ) : records.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Database size={48} className="mx-auto mb-4 opacity-50" />
          <p>Aucun enregistrement dans la file d'attente</p>
        </div>
      ) : (
        <>
          {/* Mobile: Card Layout */}
          <div className="md:hidden space-y-3">
            {records.map((record) => (
              <Card key={record.id} data-testid={`queue-record-${record.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-start gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={selectedRecords.includes(record.id)}
                      onChange={() => toggleSelect(record.id)}
                      className="rounded mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">
                        {record.normalized_address || record.address_raw}
                      </p>
                      <div className="flex items-center gap-2 flex-wrap mt-1">
                        <StatusBadge status={record.status} />
                        <ObservationTypeBadge type={record.observation_type} />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                    <div>
                      <span className="text-muted-foreground">Arr.:</span>
                      <span className="ml-1">{record.arrondissement || '-'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Surface:</span>
                      <span className="ml-1">{record.surface_sqm ? `${record.surface_sqm} m²` : '-'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Prix:</span>
                      <span className="ml-1 font-tabular">
                        {record.asking_price || record.sold_price
                          ? formatCurrency(record.asking_price || record.sold_price)
                          : '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Confiance:</span>
                      <span className="ml-1">{Math.round((record.final_usability_score || 0.5) * 100)}%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t">
                    <span className="text-xs text-muted-foreground">{record.source_id}</span>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setDetailRecord(record)} title="Voir détails">
                        <Eye size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setEditRecord(record)} title="Modifier">
                        <PencilSimple size={16} />
                      </Button>
                      {['pending', 'needs_review'].includes(record.status) && (
                        <>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-emerald-600" onClick={() => handleApprove(record.id)} title="Approuver">
                            <CheckCircle size={16} />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-600" onClick={() => handleReject(record.id)} title="Rejeter">
                            <XCircle size={16} />
                          </Button>
                        </>
                      )}
                      {record.status === 'approved' && !record.linked_comparable_id && (
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-blue-600" onClick={() => handleConvert(record.id)} title="Convertir en comparable">
                          <ArrowRight size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Desktop: Table Layout */}
          <Card className="hidden md:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="py-3 px-4 text-left">
                      <input
                        type="checkbox"
                        checked={selectedRecords.length === records.length && records.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded"
                      />
                    </th>
                    <th className="py-3 px-4 text-left font-medium">Address</th>
                    <th className="py-3 px-4 text-left font-medium">Arr.</th>
                    <th className="py-3 px-4 text-left font-medium">Surface</th>
                    <th className="py-3 px-4 text-left font-medium">Price</th>
                    <th className="py-3 px-4 text-left font-medium">Type</th>
                    <th className="py-3 px-4 text-left font-medium">Source</th>
                    <th className="py-3 px-4 text-left font-medium">Confidence</th>
                    <th className="py-3 px-4 text-left font-medium">Status</th>
                    <th className="py-3 px-4 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((record) => (
                    <tr 
                      key={record.id} 
                      className="border-b hover:bg-muted/30"
                      data-testid={`queue-record-desktop-${record.id}`}
                    >
                      <td className="py-3 px-4">
                        <input
                          type="checkbox"
                          checked={selectedRecords.includes(record.id)}
                          onChange={() => toggleSelect(record.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-medium truncate max-w-[200px]">
                          {record.normalized_address || record.address_raw}
                        </div>
                      </td>
                      <td className="py-3 px-4">{record.arrondissement || '-'}</td>
                      <td className="py-3 px-4">
                        {record.surface_sqm ? `${record.surface_sqm} m²` : '-'}
                      </td>
                      <td className="py-3 px-4">
                        {record.asking_price || record.sold_price
                          ? formatCurrency(record.asking_price || record.sold_price)
                          : '-'}
                      </td>
                      <td className="py-3 px-4">
                        <ObservationTypeBadge type={record.observation_type} />
                      </td>
                      <td className="py-3 px-4 text-muted-foreground text-xs">{record.source_id}</td>
                      <td className="py-3 px-4">
                        <ConfidenceBar score={record.final_usability_score || 0.5} />
                      </td>
                      <td className="py-3 px-4">
                        <StatusBadge status={record.status} />
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setDetailRecord(record)} title="Voir détails">
                            <Eye size={16} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setEditRecord(record)} title="Modifier">
                            <PencilSimple size={16} />
                          </Button>
                          {['pending', 'needs_review'].includes(record.status) && (
                            <>
                              <Button variant="ghost" size="sm" className="text-emerald-600" onClick={() => handleApprove(record.id)} title="Approuver">
                                <CheckCircle size={16} />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleReject(record.id)} title="Rejeter">
                                <XCircle size={16} />
                              </Button>
                            </>
                          )}
                          {record.status === 'approved' && !record.linked_comparable_id && (
                            <Button variant="ghost" size="sm" className="text-blue-600" onClick={() => handleConvert(record.id)} title="Convertir en comparable">
                              <ArrowRight size={16} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* Detail Modal */}
      {detailRecord && (
        <RecordDetailModal record={detailRecord} onClose={() => setDetailRecord(null)} />
      )}

      {/* Edit Modal */}
      {editRecord && (
        <RecordEditModal
          record={editRecord}
          onSave={handleSaveEdit}
          onClose={() => setEditRecord(null)}
        />
      )}
    </div>
  );
};

// ============== RECORD DETAIL MODAL ==============

const RecordDetailModal = ({ record, onClose }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Record Details</CardTitle>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X size={20} />
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Status & Confidence */}
        <div className="flex items-center gap-4">
          <StatusBadge status={record.status} />
          <ObservationTypeBadge type={record.observation_type} />
          <ConfidenceBar score={record.final_usability_score || 0.5} label="Usability" />
        </div>

        {/* Address */}
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-1">Address</h4>
          <p>{record.normalized_address || record.address_raw}</p>
          <p className="text-sm text-muted-foreground">
            {record.arrondissement && `${record.arrondissement} arr.`}
            {record.postal_code && ` - ${record.postal_code}`}
          </p>
        </div>

        {/* Property Details */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <span className="text-xs text-muted-foreground">Surface</span>
            <p>{record.surface_sqm ? `${record.surface_sqm} m²` : '-'}</p>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Rooms</span>
            <p>{record.room_count || '-'}</p>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Floor</span>
            <p>{record.floor ?? '-'}</p>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Type</span>
            <p className="capitalize">{record.property_type || '-'}</p>
          </div>
        </div>

        {/* Pricing */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-muted rounded-lg p-4">
          <div>
            <span className="text-xs text-muted-foreground">Asking Price</span>
            <p className="font-medium">
              {record.asking_price ? formatCurrency(record.asking_price) : '-'}
            </p>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Sold Price</span>
            <p className="font-medium">
              {record.sold_price ? formatCurrency(record.sold_price) : '-'}
            </p>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Price/m²</span>
            <p className="font-medium">
              {record.price_per_sqm ? formatCurrency(record.price_per_sqm) : '-'}
            </p>
          </div>
        </div>

        {/* Amenities */}
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Amenities</h4>
          <div className="flex flex-wrap gap-2">
            {record.has_elevator && <Badge variant="secondary">Elevator</Badge>}
            {record.has_balcony && <Badge variant="secondary">Balcony</Badge>}
            {record.has_terrace && <Badge variant="secondary">Terrace</Badge>}
            {record.has_parking && <Badge variant="secondary">Parking</Badge>}
          </div>
        </div>

        {/* Source Info */}
        <div className="text-sm space-y-1">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Source:</span>
            <span>{record.source_name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Batch ID:</span>
            <span className="text-xs font-mono">{record.import_batch_id}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Imported:</span>
            <span>{formatDate(record.imported_at)}</span>
          </div>
        </div>

        {/* Notes */}
        {record.notes && (
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-1">Notes</h4>
            <p className="text-sm">{record.notes}</p>
          </div>
        )}
      </CardContent>
    </Card>
  </div>
);

// ============== RECORD EDIT MODAL ==============

const RecordEditModal = ({ record, onSave, onClose }) => {
  const [form, setForm] = useState({
    normalized_address: record.normalized_address || record.address_raw || '',
    arrondissement: record.arrondissement || '',
    postal_code: record.postal_code || '',
    surface_sqm: record.surface_sqm || '',
    room_count: record.room_count || '',
    floor: record.floor ?? '',
    asking_price: record.asking_price || '',
    sold_price: record.sold_price || '',
    observation_type: record.observation_type || 'asking',
    has_elevator: record.has_elevator || false,
    has_balcony: record.has_balcony || false,
    has_terrace: record.has_terrace || false,
    has_parking: record.has_parking || false,
    notes: record.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updates = { ...form };
      if (updates.surface_sqm) updates.surface_sqm = parseFloat(updates.surface_sqm);
      if (updates.room_count) updates.room_count = parseInt(updates.room_count);
      if (updates.floor !== '') updates.floor = parseInt(updates.floor);
      if (updates.asking_price) updates.asking_price = parseFloat(updates.asking_price);
      if (updates.sold_price) updates.sold_price = parseFloat(updates.sold_price);
      
      await onSave(record.id, updates);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Modifier l'enregistrement</CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X size={20} />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Adresse</Label>
              <Input
                value={form.normalized_address}
                onChange={(e) => setForm({ ...form, normalized_address: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Arrondissement</Label>
                <Input
                  value={form.arrondissement}
                  onChange={(e) => setForm({ ...form, arrondissement: e.target.value })}
                />
              </div>
              <div>
                <Label>Code postal</Label>
                <Input
                  value={form.postal_code}
                  onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Surface (m²)</Label>
                <Input
                  type="number"
                  value={form.surface_sqm}
                  onChange={(e) => setForm({ ...form, surface_sqm: e.target.value })}
                />
              </div>
              <div>
                <Label>Pièces</Label>
                <Input
                  type="number"
                  value={form.room_count}
                  onChange={(e) => setForm({ ...form, room_count: e.target.value })}
                />
              </div>
              <div>
                <Label>Étage</Label>
                <Input
                  type="number"
                  value={form.floor}
                  onChange={(e) => setForm({ ...form, floor: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prix demandé</Label>
                <Input
                  type="number"
                  value={form.asking_price}
                  onChange={(e) => setForm({ ...form, asking_price: e.target.value })}
                />
              </div>
              <div>
                <Label>Prix de vente</Label>
                <Input
                  type="number"
                  value={form.sold_price}
                  onChange={(e) => setForm({ ...form, sold_price: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Observation Type</Label>
              <select
                value={form.observation_type}
                onChange={(e) => setForm({ ...form, observation_type: e.target.value })}
                className="w-full border border-input rounded-lg px-3 py-2"
              >
                <option value="asking">Asking</option>
                <option value="sold">Sold</option>
                <option value="benchmark">Benchmark</option>
                <option value="estimated">Estimated</option>
              </select>
            </div>

            <div>
              <Label>Amenities</Label>
              <div className="flex flex-wrap gap-4 mt-2">
                {[
                  { key: 'has_elevator', label: 'Elevator' },
                  { key: 'has_balcony', label: 'Balcony' },
                  { key: 'has_terrace', label: 'Terrace' },
                  { key: 'has_parking', label: 'Parking' },
                ].map((amenity) => (
                  <label key={amenity.key} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form[amenity.key]}
                      onChange={(e) => setForm({ ...form, [amenity.key]: e.target.checked })}
                      className="rounded"
                    />
                    {amenity.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                className="w-full border border-input rounded-lg px-3 py-2"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== IMPORT HISTORY ==============

const ImportHistory = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBatches = async () => {
      try {
        const data = await importApi.getBatches();
        setBatches(data.batches || []);
      } catch (err) {
        toast.error('Échec du chargement de l\'historique');
      } finally {
        setLoading(false);
      }
    };
    fetchBatches();
  }, []);

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Chargement...</div>;
  }

  if (batches.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <ClockClockwise size={48} className="mx-auto mb-4 opacity-50" />
        <p>Aucun historique d'importation</p>
      </div>
    );
  }

  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="py-3 px-4 text-left font-medium">ID Lot</th>
              <th className="py-3 px-4 text-left font-medium">Source</th>
              <th className="py-3 px-4 text-left font-medium">Type</th>
              <th className="py-3 px-4 text-left font-medium">Date</th>
              <th className="py-3 px-4 text-center font-medium">Total</th>
              <th className="py-3 px-4 text-center font-medium">Approuvé</th>
              <th className="py-3 px-4 text-center font-medium">En attente</th>
              <th className="py-3 px-4 text-center font-medium">Rejected</th>
            </tr>
          </thead>
          <tbody>
            {batches.map((batch) => (
              <tr key={batch.id} className="border-b hover:bg-muted/30">
                <td className="py-3 px-4 font-mono text-xs">{batch.id.slice(0, 8)}...</td>
                <td className="py-3 px-4">{batch.source_name}</td>
                <td className="py-3 px-4">{batch.import_type}</td>
                <td className="py-3 px-4">{formatDate(batch.created_at)}</td>
                <td className="py-3 px-4 text-center">{batch.total_records}</td>
                <td className="py-3 px-4 text-center text-emerald-600">{batch.approved_count}</td>
                <td className="py-3 px-4 text-center text-amber-600">{batch.pending_count}</td>
                <td className="py-3 px-4 text-center text-red-600">{batch.rejected_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// ============== MAIN COMPONENT ==============

export const DataIngestion = () => {
  const [activeTab, setActiveTab] = useState('sources');
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [queueStats, setQueueStats] = useState({});

  const fetchSources = useCallback(async () => {
    try {
      const data = await importApi.getSources();
      setSources(data);
    } catch (err) {
      console.error('Failed to load sources:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchQueueStats = useCallback(async () => {
    try {
      const data = await importApi.getQueue({ limit: 1 });
      setQueueStats(data.stats || {});
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchSources();
    fetchQueueStats();
  }, [fetchSources, fetchQueueStats]);

  const handleRefresh = () => {
    fetchSources();
    fetchQueueStats();
  };

  const tabs = [
    { id: 'sources', label: 'Sources de Données', icon: Database },
    { id: 'import', label: 'Import CSV', icon: Upload },
    { id: 'queue', label: 'File d\'Attente', icon: ListChecks, count: (queueStats.pending || 0) + (queueStats.needs_review || 0) },
    { id: 'history', label: 'Historique', icon: ClockClockwise },
  ];

  return (
    <div className="space-y-6" data-testid="data-ingestion-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Import de Données</h1>
          <p className="text-muted-foreground mt-1">
            Importer et gérer les sources de données externes du marché
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {/* Content */}
      <div className="min-h-[400px]">
        {activeTab === 'sources' && (
          <DataSourcesPanel sources={sources} loading={loading} onRefresh={handleRefresh} />
        )}

        {activeTab === 'import' && (
          <CSVImportWizard onImportComplete={handleRefresh} />
        )}

        {activeTab === 'queue' && (
          <ReviewQueue onRecordAction={handleRefresh} />
        )}

        {activeTab === 'history' && (
          <ImportHistory />
        )}
      </div>

      {/* Page Help */}
      <PageHelpBlock 
        pageKey="data-ingestion" 
        content={pageExplanations.dataIngestion} 
      />
    </div>
  );
};

export default DataIngestion;
